<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import CrmDealsAPI from 'dashboard/api/crm/deals';
import CrmPipelinesAPI from 'dashboard/api/crm/pipelines';
import CrmLossReasonsAPI from 'dashboard/api/crm/lossReasons';
import CrmDealTasksAPI from 'dashboard/api/crm/dealTasks';
import DateTimePicker from 'dashboard/components/ui/DateTimePicker.vue';

const props = defineProps({
    conversationId: {
        type: [Number, String],
        required: true,
    },
});

const store = useStore();
const router = useRouter();
const currentChat = useMapGetter('getSelectedChat');
const currentUser = useMapGetter('getCurrentUser');

const deal = ref(null);
const stages = ref([]);
const lossReasons = ref([]);
const contactDeals = ref([]);
const isLoading = ref(false);
const isMoving = ref(false);
const isUpdatingStatus = ref(false);
const isLinking = ref(false);
const showLossReasonPicker = ref(false);
const showDealPicker = ref(false);
const selectedLossReasonId = ref(null);
const selectedDealId = ref(null);
const showChildDealForm = ref(false);
const isCreatingChild = ref(false);
const childDealForm = ref({ name: '', pipeline_id: null, stage_id: null, user_id: null });
const allPipelines = ref([]);
const childStages = ref([]);
const childAgents = ref([]);

// Create new deal (when no deal linked)
const showCreateDealForm = ref(false);
const isCreatingNewDeal = ref(false);
const newDealForm = ref({ name: '', pipeline_id: null, stage_id: null, user_id: null });
const newDealStages = ref([]);

// Tasks
const dealTasks = ref([]);
const isFetchingTasks = ref(false);
const showTaskForm = ref(false);
const isCreatingTask = ref(false);
const TASK_CATEGORIES = [
    { key: 'call', label: 'Ligação', icon: '📞' },
    { key: 'message', label: 'Mensagem', icon: '💬' },
    { key: 'email', label: 'Email', icon: '📧' },
    { key: 'meeting', label: 'Reunião', icon: '🤝' },
    { key: 'other', label: 'Outros', icon: '📋' },
];
const newTaskForm = ref({ title: '', category: 'call', due_at: null });

const crmDealId = computed(() => currentChat.value?.crm_deal_id);
const currentStageId = computed(() => deal.value?.crm_pipeline_stage_id);
const contactId = computed(() => currentChat.value?.meta?.sender?.id);

const fetchDeal = async () => {
    if (!crmDealId.value) {
        deal.value = null;
        stages.value = [];
        dealTasks.value = [];
        return;
    }
    isLoading.value = true;
    try {
        const { data } = await CrmDealsAPI.show(crmDealId.value);
        deal.value = data;
        await fetchStages(data.crm_pipeline_id);
        fetchDealTasks();
    } catch {
        deal.value = null;
    } finally {
        isLoading.value = false;
    }
};

const fetchDealTasks = async () => {
    if (!deal.value) return;
    isFetchingTasks.value = true;
    try {
        const { data } = await CrmDealTasksAPI.getAll(deal.value.id);
        dealTasks.value = data || [];
    } catch {
        dealTasks.value = [];
    } finally {
        isFetchingTasks.value = false;
    }
};

const createTask = async () => {
    if (!newTaskForm.value.title || !deal.value || isCreatingTask.value) return;
    isCreatingTask.value = true;
    try {
        const payload = {
            task: {
                title: newTaskForm.value.title,
                category: newTaskForm.value.category,
                due_at: newTaskForm.value.due_at || null,
            },
        };
        await CrmDealTasksAPI.createTask(deal.value.id, payload);
        newTaskForm.value = { title: '', category: 'call', due_at: null };
        showTaskForm.value = false;
        await fetchDealTasks();
    } catch {
        // keep current state
    } finally {
        isCreatingTask.value = false;
    }
};

const toggleTask = async (task) => {
    try {
        await CrmDealTasksAPI.updateTask(deal.value.id, task.id, {
            task: { completed: !task.completed },
        });
        task.completed = !task.completed;
    } catch {
        // keep current state
    }
};

const deleteTask = async (task) => {
    if (!confirm('Excluir esta tarefa?')) return;
    try {
        await CrmDealTasksAPI.deleteTask(deal.value.id, task.id);
        dealTasks.value = dealTasks.value.filter(t => t.id !== task.id);
    } catch {
        // keep current state
    }
};

const isOverdue = (task) => {
    if (task.completed || !task.due_at) return false;
    return new Date(task.due_at) < new Date();
};

const pendingTasks = computed(() => dealTasks.value.filter(t => !t.completed));
const completedTasks = computed(() => dealTasks.value.filter(t => t.completed));

const fetchStages = async pipelineId => {
    if (!pipelineId) return;
    try {
        const { data } = await CrmPipelinesAPI.show(pipelineId);
        const stagesData = data.stages || data;
        stages.value = (Array.isArray(stagesData) ? stagesData : []).sort(
            (a, b) => a.position - b.position
        );
    } catch {
        try {
            const { data: stagesResp } = await axios.get(
                `${CrmPipelinesAPI.url}/${pipelineId}/stages`
            );
            stages.value = (stagesResp || []).sort(
                (a, b) => a.position - b.position
            );
        } catch {
            stages.value = [];
        }
    }
};

const fetchLossReasons = async () => {
    try {
        const { data } = await CrmLossReasonsAPI.get();
        lossReasons.value = data || [];
    } catch {
        lossReasons.value = [];
    }
};

const fetchContactDeals = async () => {
    if (!contactId.value) return;
    try {
        const { data } = await axios.get(CrmDealsAPI.url, {
            params: { contact_id: contactId.value },
        });
        contactDeals.value = data.data || [];
    } catch {
        contactDeals.value = [];
    }
};

const onStageChange = async event => {
    const newStageId = Number(event.target.value);
    if (!deal.value || newStageId === deal.value.crm_pipeline_stage_id) return;
    isMoving.value = true;
    try {
        await CrmDealsAPI.move(deal.value.id, newStageId);
        deal.value.crm_pipeline_stage_id = newStageId;
    } catch {
        // keep current state
    } finally {
        isMoving.value = false;
    }
};

const markAsWon = async () => {
    if (!deal.value || isUpdatingStatus.value) return;
    isUpdatingStatus.value = true;
    try {
        await CrmDealsAPI.update(deal.value.id, { status: 'won' });
        deal.value.status = 'won';
        showLossReasonPicker.value = false;
    } catch {
        // keep current state
    } finally {
        isUpdatingStatus.value = false;
    }
};

const onLostClick = () => {
    if (deal.value?.status !== 'open') return;
    showLossReasonPicker.value = true;
    if (!lossReasons.value.length) fetchLossReasons();
};

const confirmLost = async () => {
    if (!deal.value || isUpdatingStatus.value) return;
    isUpdatingStatus.value = true;
    try {
        const params = { status: 'lost' };
        if (selectedLossReasonId.value) {
            params.crm_loss_reason_id = selectedLossReasonId.value;
        }
        await CrmDealsAPI.update(deal.value.id, params);
        deal.value.status = 'lost';
        deal.value.crm_loss_reason_id = selectedLossReasonId.value;
        showLossReasonPicker.value = false;
    } catch {
        // keep current state
    } finally {
        isUpdatingStatus.value = false;
    }
};

const cancelLost = () => {
    showLossReasonPicker.value = false;
    selectedLossReasonId.value = null;
};

const reopenDeal = async () => {
    if (!deal.value || isUpdatingStatus.value) return;
    isUpdatingStatus.value = true;
    try {
        await CrmDealsAPI.update(deal.value.id, {
            status: 'open',
            crm_loss_reason_id: null,
        });
        deal.value.status = 'open';
        deal.value.crm_loss_reason_id = null;
    } catch {
        // keep current state
    } finally {
        isUpdatingStatus.value = false;
    }
};

// Deal linking
const openDealPicker = () => {
    showDealPicker.value = true;
    selectedDealId.value = null;
    fetchContactDeals();
};

const cancelDealPicker = () => {
    showDealPicker.value = false;
    selectedDealId.value = null;
};

const linkToDeal = async () => {
    if (!selectedDealId.value || isLinking.value) return;
    isLinking.value = true;
    try {
        // If currently linked to a deal, unlink first
        if (crmDealId.value) {
            await CrmDealsAPI.unlinkConversation(
                crmDealId.value,
                props.conversationId
            );
        }
        // Link to the new deal
        await CrmDealsAPI.linkConversation(
            selectedDealId.value,
            props.conversationId
        );
        // Update the local conversation store
        store.commit('updateConversationCrmDealId', {
            conversationId: currentChat.value.id,
            crmDealId: Number(selectedDealId.value),
        });
        showDealPicker.value = false;
        selectedDealId.value = null;
        // Refresh deal data
        await fetchDeal();
    } catch {
        // keep current state
    } finally {
        isLinking.value = false;
    }
};

const unlinkDeal = async () => {
    if (!crmDealId.value || isLinking.value) return;
    isLinking.value = true;
    try {
        await CrmDealsAPI.unlinkConversation(
            crmDealId.value,
            props.conversationId
        );
        store.commit('updateConversationCrmDealId', {
            conversationId: currentChat.value.id,
            crmDealId: null,
        });
        deal.value = null;
        stages.value = [];
    } catch {
        // keep current state
    } finally {
        isLinking.value = false;
    }
};

// Child deals
const openChildDealForm = async () => {
    showChildDealForm.value = true;
    childDealForm.value = { name: '', pipeline_id: null, stage_id: null, user_id: null };
    childStages.value = [];
    await loadPipelinesAndAgents();
};

const cancelChildDealForm = () => {
    showChildDealForm.value = false;
};

const onChildPipelineChange = async (event) => {
    const pipelineId = Number(event.target.value);
    childDealForm.value.pipeline_id = pipelineId;
    childDealForm.value.stage_id = null;
    childStages.value = [];
    if (!pipelineId) return;
    try {
        const { data } = await CrmPipelinesAPI.show(pipelineId);
        const stagesData = data.stages || data;
        childStages.value = (Array.isArray(stagesData) ? stagesData : []).sort(
            (a, b) => a.position - b.position
        );
        if (childStages.value.length) {
            childDealForm.value.stage_id = childStages.value[0].id;
        }
    } catch {
        childStages.value = [];
    }
};

const createChildDeal = async () => {
    if (!childDealForm.value.name || !childDealForm.value.pipeline_id || !childDealForm.value.stage_id) return;
    if (!deal.value || isCreatingChild.value) return;
    isCreatingChild.value = true;
    try {
        await store.dispatch('crmDeals/create', {
            deal: {
                name: childDealForm.value.name,
                crm_pipeline_id: childDealForm.value.pipeline_id,
                crm_pipeline_stage_id: childDealForm.value.stage_id,
                contact_id: deal.value.contact_id,
                parent_deal_id: deal.value.id,
                user_id: childDealForm.value.user_id || null,
                amount: 0,
                status: 'open',
            },
        });
        showChildDealForm.value = false;
        await fetchDeal();
    } catch {
        // keep current state
    } finally {
        isCreatingChild.value = false;
    }
};

// Create new deal (from conversation)
const loadPipelinesAndAgents = async () => {
    if (!allPipelines.value.length) {
        try {
            const { data } = await CrmPipelinesAPI.get();
            allPipelines.value = data || [];
        } catch {
            allPipelines.value = [];
        }
    }
    if (!childAgents.value.length) {
        try {
            await store.dispatch('agents/get');
            childAgents.value = store.getters['agents/getAgents'] || [];
        } catch {
            childAgents.value = [];
        }
    }
};

const openCreateDealForm = async () => {
    showCreateDealForm.value = true;
    showDealPicker.value = false;
    newDealForm.value = { name: '', pipeline_id: null, stage_id: null, user_id: currentUser.value?.id || null };
    newDealStages.value = [];
    await loadPipelinesAndAgents();
};

const cancelCreateDealForm = () => {
    showCreateDealForm.value = false;
};

const onNewDealPipelineChange = async (event) => {
    const pipelineId = Number(event.target.value);
    newDealForm.value.pipeline_id = pipelineId;
    newDealForm.value.stage_id = null;
    newDealStages.value = [];
    if (!pipelineId) return;
    try {
        const { data } = await CrmPipelinesAPI.show(pipelineId);
        const stagesData = data.stages || data;
        newDealStages.value = (Array.isArray(stagesData) ? stagesData : []).sort(
            (a, b) => a.position - b.position
        );
        if (newDealStages.value.length) {
            newDealForm.value.stage_id = newDealStages.value[0].id;
        }
    } catch {
        newDealStages.value = [];
    }
};

const createNewDeal = async () => {
    if (!newDealForm.value.name || !newDealForm.value.pipeline_id || !newDealForm.value.stage_id) return;
    if (isCreatingNewDeal.value) return;
    isCreatingNewDeal.value = true;
    try {
        const { data } = await CrmDealsAPI.create({
            deal: {
                name: newDealForm.value.name,
                crm_pipeline_id: newDealForm.value.pipeline_id,
                crm_pipeline_stage_id: newDealForm.value.stage_id,
                contact_id: contactId.value,
                user_id: newDealForm.value.user_id || null,
                amount: 0,
                status: 'open',
            },
        });
        // Link the conversation to the new deal
        const newDealId = data.id;
        await CrmDealsAPI.linkConversation(newDealId, props.conversationId);
        store.commit('updateConversationCrmDealId', {
            conversationId: currentChat.value.id,
            crmDealId: newDealId,
        });
        showCreateDealForm.value = false;
        await fetchDeal();
    } catch {
        // keep current state
    } finally {
        isCreatingNewDeal.value = false;
    }
};

watch(crmDealId, () => fetchDeal(), { immediate: false });

onMounted(() => {
    fetchDeal();
});
</script>

<template>
    <div class="px-1 py-2">
        <div v-if="isLoading" class="flex items-center justify-center py-6">
            <div class="flex items-center gap-2">
                <svg class="animate-spin h-4 w-4 text-n-blue-10" xmlns="http://www.w3.org/2000/svg" fill="none"
                    viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                <span class="text-xs text-n-slate-10">Carregando deal...</span>
            </div>
        </div>

        <div v-else-if="!crmDealId || !deal">
            <div v-if="!showDealPicker && !showCreateDealForm"
                class="flex flex-col items-center justify-center py-4 gap-3">
                <div class="w-10 h-10 rounded-full bg-n-alpha-2 flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-n-slate-9" fill="none"
                        viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                        <path stroke-linecap="round" stroke-linejoin="round"
                            d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m9.193-9.193a4.5 4.5 0 016.364 6.364l-4.5 4.5a4.5 4.5 0 01-7.244-1.242" />
                    </svg>
                </div>
                <span class="text-xs text-n-slate-10">Nenhuma oportunidade vinculada</span>
                <div class="flex gap-2">
                    <button
                        class="text-xs font-semibold py-1.5 px-4 rounded-lg bg-n-blue-3 text-n-blue-11 hover:bg-n-blue-4 active:bg-n-blue-5 transition-all duration-150"
                        @click="openDealPicker">
                        + Vincular Oportunidade
                    </button>
                    <button
                        class="text-xs font-semibold py-1.5 px-4 rounded-lg bg-n-blue-3 text-n-blue-11 hover:bg-n-blue-4 active:bg-n-blue-5 transition-all duration-150 cursor-pointer"
                        @click="openCreateDealForm">
                        + Criar Oportunidade
                    </button>
                </div>
            </div>

            <div v-if="showCreateDealForm"
                class="flex flex-col gap-1.5 p-2.5 rounded-lg border border-n-weak bg-n-solid-2 shadow-sm">
                <label
                    class="text-[10px] font-semibold text-n-blue-11 uppercase tracking-wider flex items-center gap-1.5">
                    <span class="i-lucide-plus-circle size-3.5" />
                    Criar Nova Oportunidade
                </label>
                <div>
                    <label class="text-[10px] font-medium text-n-slate-10 block mb-0.5">Nome *</label>
                    <input v-model="newDealForm.name"
                        class="w-full text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 py-1.5 px-2.5 focus:outline-none focus:ring-2 focus:ring-n-blue-7 focus:border-n-blue-8 transition-all placeholder:text-n-slate-8"
                        placeholder="Nome da oportunidade" />
                </div>
                <div>
                    <label class="text-[10px] font-medium text-n-slate-10 block mb-0.5">Funil *</label>
                    <select :value="newDealForm.pipeline_id"
                        class="w-full text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 py-1.5 px-2.5 cursor-pointer focus:outline-none focus:ring-2 focus:ring-n-blue-7 focus:border-n-blue-8 transition-all"
                        style="-webkit-appearance: none; appearance: none; background-image: none;"
                        @change="onNewDealPipelineChange">
                        <option :value="null">Selecione um funil...</option>
                        <option v-for="p in allPipelines" :key="p.id" :value="p.id">{{ p.name }}</option>
                    </select>
                </div>
                <div v-if="newDealStages.length">
                    <label class="text-[10px] font-medium text-n-slate-10 block mb-0.5">Etapa *</label>
                    <select v-model="newDealForm.stage_id"
                        class="w-full text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 py-2 px-3 cursor-pointer focus:outline-none focus:ring-2 focus:ring-n-blue-7 focus:border-n-blue-8 transition-all"
                        style="-webkit-appearance: none; appearance: none; background-image: none;">
                        <option v-for="s in newDealStages" :key="s.id" :value="s.id">{{ s.name }}</option>
                    </select>
                </div>
                <div>
                    <label class="text-[10px] font-medium text-n-slate-10 block mb-0.5">Responsável</label>
                    <select v-model="newDealForm.user_id"
                        class="w-full text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 py-2 px-3 cursor-pointer focus:outline-none focus:ring-2 focus:ring-n-blue-7 focus:border-n-blue-8 transition-all"
                        style="-webkit-appearance: none; appearance: none; background-image: none;">
                        <option :value="null">Sem responsável</option>
                        <option v-for="a in childAgents" :key="a.id" :value="a.id">{{ a.name }}</option>
                    </select>
                </div>
                <div class="flex gap-2">
                    <button
                        :disabled="!newDealForm.name || !newDealForm.pipeline_id || !newDealForm.stage_id || isCreatingNewDeal"
                        class="flex-1 text-xs font-semibold py-2 px-3 rounded-lg bg-n-blue-9 text-white hover:bg-n-blue-10 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                        @click="createNewDeal">
                        {{ isCreatingNewDeal ? 'Criando...' : 'Criar e Vincular' }}
                    </button>
                    <button
                        class="text-xs font-medium py-2 px-3 rounded-lg bg-n-alpha-3 text-n-slate-11 hover:bg-n-alpha-4 transition-all"
                        @click="cancelCreateDealForm">
                        Cancelar
                    </button>
                </div>
            </div>

            <div v-if="showDealPicker"
                class="flex flex-col gap-2.5 p-3 rounded-lg border border-n-weak bg-n-solid-2 shadow-sm">
                <label class="text-[10px] font-semibold text-n-slate-10 uppercase tracking-wider">
                    Selecionar Deal do Contato
                </label>
                <select v-model="selectedDealId"
                    class="w-full text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 py-2 px-3 cursor-pointer hover:border-n-slate-7 focus:outline-none focus:ring-2 focus:ring-n-blue-7 focus:border-n-blue-8 transition-all"
                    style="-webkit-appearance: none; appearance: none; background-image: none;">
                    <option :value="null">Escolha uma deal...</option>
                    <option v-for="d in contactDeals" :key="d.id" :value="d.id">
                        {{ d.name }} — {{ d.status === 'open' ? 'Aberta' : d.status === 'won' ? 'Ganha' : 'Perdida'
                        }}
                    </option>
                </select>
                <div class="flex gap-2 pt-1">
                    <button :disabled="!selectedDealId || isLinking"
                        class="flex-1 text-xs font-semibold py-2 px-3 rounded-lg bg-n-blue-9 text-white hover:bg-n-blue-10 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                        @click="linkToDeal">
                        Vincular
                    </button>
                    <button
                        class="text-xs font-medium py-2 px-3 rounded-lg bg-n-alpha-3 text-n-slate-11 hover:bg-n-alpha-4 transition-all"
                        @click="cancelDealPicker">
                        Cancelar
                    </button>
                </div>
            </div>
        </div>

        <div v-else class="flex flex-col gap-0">

            <div class="rounded-lg border border-n-weak bg-n-solid-2 p-3 shadow-sm">
                <div class="flex items-center gap-2 mb-2">
                    <div class="flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center" :class="{
                        'bg-n-blue-3': deal.status === 'open',
                        'bg-n-green-3': deal.status === 'won',
                        'bg-n-ruby-3': deal.status === 'lost',
                    }">
                        <svg v-if="deal.status === 'open'" xmlns="http://www.w3.org/2000/svg"
                            class="h-3.5 w-3.5 text-n-blue-11" fill="none" viewBox="0 0 24 24" stroke="currentColor"
                            stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round"
                                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <svg v-else-if="deal.status === 'won'" xmlns="http://www.w3.org/2000/svg"
                            class="h-3.5 w-3.5 text-n-green-11" fill="none" viewBox="0 0 24 24" stroke="currentColor"
                            stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round"
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5 text-n-ruby-11" fill="none"
                            viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round"
                                d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <div class="flex-1 min-w-0">
                        <p class="text-sm font-semibold text-n-slate-12 truncate leading-tight">{{ deal.name }}</p>
                        <p class="text-[10px] text-n-slate-10 leading-tight mt-0.5">#{{ deal.id }}</p>
                    </div>
                    <button
                        class="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-lg bg-n-alpha-2 text-n-slate-10 hover:bg-n-blue-3 hover:text-n-blue-11 transition-all"
                        title="Ver deal no CRM"
                        @click="router.push({ name: 'crm_deal_show', params: { accountId: store.getters.getCurrentAccountId, dealId: deal.id } })">
                        <span class="i-lucide-external-link size-3.5" />
                    </button>
                </div>
                <div class="flex items-center gap-1.5 mb-2">
                    <button
                        class="text-[10px] font-medium px-2 py-1 rounded-md bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3 hover:text-n-slate-12 transition-all"
                        @click="openDealPicker">
                        Trocar
                    </button>
                    <button
                        class="text-[10px] font-medium px-2 py-1 rounded-md bg-n-ruby-2 text-n-ruby-10 hover:bg-n-ruby-3 hover:text-n-ruby-11 transition-all"
                        @click="unlinkDeal">
                        Desvincular
                    </button>
                </div>

                <div class="flex items-center gap-2 mb-2.5">
                    <span class="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full"
                        :class="{
                            'bg-n-blue-3 text-n-blue-11': deal.status === 'open',
                            'bg-n-green-3 text-n-green-11': deal.status === 'won',
                            'bg-n-ruby-3 text-n-ruby-11': deal.status === 'lost',
                        }">
                        <span class="w-1.5 h-1.5 rounded-full" :class="{
                            'bg-n-blue-9': deal.status === 'open',
                            'bg-n-green-9': deal.status === 'won',
                            'bg-n-ruby-9': deal.status === 'lost',
                        }" />
                        {{ deal.status === 'open' ? 'Aberta' : deal.status === 'won' ? 'Ganha' : 'Perdida' }}
                    </span>
                    <span v-if="deal.status === 'lost' && deal.loss_reason_title"
                        class="text-[10px] text-n-slate-10 italic truncate">
                        {{ deal.loss_reason_title }}
                    </span>
                    <span v-if="deal.amount" class="ml-auto text-xs font-bold text-n-slate-12">
                        R$ {{ Number(deal.amount).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) }}
                    </span>
                </div>

                <div v-if="deal.status === 'open' && stages.length > 1" class="mb-2.5">
                    <div class="flex items-center justify-between mb-1.5">
                        <label class="text-[10px] font-semibold text-n-slate-10 uppercase tracking-wider">Etapa do
                            Funil</label>
                        <span class="text-[10px] text-n-slate-10">
                            {{stages.findIndex(s => s.id === currentStageId) + 1}}/{{ stages.length }}
                        </span>
                    </div>
                    <div class="flex gap-0.5 mb-2">
                        <div v-for="(stage, idx) in stages" :key="stage.id"
                            class="h-1.5 flex-1 rounded-full transition-all duration-300"
                            :class="idx <= stages.findIndex(s => s.id === currentStageId) ? 'bg-n-blue-9' : 'bg-n-alpha-3'" />
                    </div>
                    <select :value="currentStageId" :disabled="isMoving"
                        class="w-full text-xs rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12 py-2 px-3 cursor-pointer hover:border-n-slate-7 focus:outline-none focus:ring-2 focus:ring-n-blue-7 focus:border-n-blue-8 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        style="-webkit-appearance: none; appearance: none; background-image: none;"
                        @change="onStageChange">
                        <option v-for="stage in stages" :key="stage.id" :value="stage.id">
                            {{ stage.name }}
                        </option>
                    </select>
                </div>

                <div v-if="showDealPicker"
                    class="flex flex-col gap-2.5 p-3 rounded-lg border border-n-weak bg-n-alpha-1 mb-2.5">
                    <label class="text-[10px] font-semibold text-n-slate-10 uppercase tracking-wider">
                        Selecionar Deal do Contato
                    </label>
                    <select v-model="selectedDealId"
                        class="w-full text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 py-2 px-3 cursor-pointer hover:border-n-slate-7 focus:outline-none focus:ring-2 focus:ring-n-blue-7 focus:border-n-blue-8 transition-all"
                        style="-webkit-appearance: none; appearance: none; background-image: none;">
                        <option :value="null">Escolha uma deal...</option>
                        <option v-for="d in contactDeals" :key="d.id" :value="d.id" :disabled="d.id === deal?.id">
                            {{ d.name }} — {{ d.status === 'open' ? 'Aberta' : d.status === 'won' ? 'Ganha' :
                                'Perdida' }}
                            {{ d.id === deal?.id ? '(atual)' : '' }}
                        </option>
                    </select>
                    <div class="flex gap-2">
                        <button :disabled="!selectedDealId || isLinking"
                            class="flex-1 text-xs font-semibold py-2 px-3 rounded-lg bg-n-blue-9 text-white hover:bg-n-blue-10 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                            @click="linkToDeal">
                            Vincular
                        </button>
                        <button
                            class="text-xs font-medium py-2 px-3 rounded-lg bg-n-alpha-3 text-n-slate-11 hover:bg-n-alpha-4 transition-all"
                            @click="cancelDealPicker">
                            Cancelar
                        </button>
                    </div>
                </div>

                <div v-if="deal.status === 'open' && !showLossReasonPicker" class="flex gap-2">
                    <button :disabled="isUpdatingStatus"
                        class="crm-conv-btn-won flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold py-2 px-3 rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                        @click="markAsWon">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24"
                            stroke="currentColor" stroke-width="2.5">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                        Ganhou
                    </button>
                    <button :disabled="isUpdatingStatus"
                        class="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold py-2 px-3 rounded-lg bg-n-ruby-3 text-n-ruby-11 hover:bg-n-ruby-4 active:bg-n-ruby-5 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                        @click="onLostClick">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24"
                            stroke="currentColor" stroke-width="2.5">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        Perdeu
                    </button>
                </div>

                <div v-if="showLossReasonPicker"
                    class="flex flex-col gap-2.5 p-3 rounded-lg border border-n-ruby-6 bg-n-ruby-2">
                    <label class="text-[10px] font-semibold text-n-ruby-11 uppercase tracking-wider">
                        Motivo da Perda
                    </label>
                    <select v-model="selectedLossReasonId"
                        class="w-full text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 py-2 px-3 cursor-pointer hover:border-n-slate-7 focus:outline-none focus:ring-2 focus:ring-n-ruby-7 focus:border-n-ruby-8 transition-all"
                        style="-webkit-appearance: none; appearance: none; background-image: none;">
                        <option :value="null">Selecione um motivo...</option>
                        <option v-for="reason in lossReasons" :key="reason.id" :value="reason.id">
                            {{ reason.title }}
                        </option>
                    </select>
                    <div class="flex gap-2">
                        <button :disabled="isUpdatingStatus"
                            class="flex-1 text-xs font-semibold py-2 px-3 rounded-lg bg-n-ruby-9 text-white hover:bg-n-ruby-10 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                            @click="confirmLost">
                            Confirmar Perda
                        </button>
                        <button :disabled="isUpdatingStatus"
                            class="text-xs font-medium py-2 px-3 rounded-lg bg-n-alpha-3 text-n-slate-11 hover:bg-n-alpha-4 transition-all disabled:opacity-40"
                            @click="cancelLost">
                            Cancelar
                        </button>
                    </div>
                </div>

                <div v-if="deal.status !== 'open'" class="mt-1">
                    <button :disabled="isUpdatingStatus"
                        class="w-full flex items-center justify-center gap-1.5 text-xs font-semibold py-2 px-3 rounded-lg bg-n-alpha-3 text-n-slate-11 hover:bg-n-alpha-4 hover:text-n-slate-12 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                        @click="reopenDeal">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24"
                            stroke="currentColor" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round"
                                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Reabrir Deal
                    </button>
                </div>
            </div>

            <div v-if="deal.parent_deal" class="mt-2 rounded-lg border border-n-weak bg-n-solid-2 p-3 shadow-sm">
                <label class="text-[10px] font-semibold text-n-slate-10 uppercase tracking-wider mb-1.5 block">
                    Deal Pai
                </label>
                <div class="flex items-center gap-2">
                    <div class="flex-shrink-0 w-5 h-5 rounded flex items-center justify-center bg-n-alpha-3">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-n-slate-10" fill="none"
                            viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M5 10l7-7m0 0l7 7m-7-7v18" />
                        </svg>
                    </div>
                    <span class="text-xs font-medium text-n-slate-12 truncate">{{ deal.parent_deal.name }}</span>
                    <span class="text-[10px] font-medium px-1.5 py-0.5 rounded-full flex-shrink-0" :class="{
                        'bg-n-blue-3 text-n-blue-11': deal.parent_deal.status === 'open',
                        'bg-n-green-3 text-n-green-11': deal.parent_deal.status === 'won',
                        'bg-n-ruby-3 text-n-ruby-11': deal.parent_deal.status === 'lost',
                    }">{{ deal.parent_deal.stage_name }}</span>
                </div>
            </div>

            <div class="mt-2 rounded-lg border border-n-weak bg-n-solid-2 p-3 shadow-sm">
                <div class="flex items-center justify-between mb-2">
                    <label class="text-[10px] font-semibold text-n-slate-10 uppercase tracking-wider">
                        Oportunidades Filhas
                    </label>
                    <button class="crm-conv-tasks-add-btn" @click="openChildDealForm">
                        <span class="i-lucide-plus size-3" />
                        Nova
                    </button>
                </div>

                <div v-if="deal.child_deals && deal.child_deals.length" class="flex flex-col gap-1.5">
                    <template v-for="child in deal.child_deals" :key="child.id">
                        <div
                            class="flex items-center justify-between py-1.5 px-2 rounded-lg bg-n-alpha-1 hover:bg-n-alpha-2 transition-colors group">
                            <div class="flex items-center gap-2 min-w-0">
                                <div class="flex-shrink-0 w-1.5 h-1.5 rounded-full" :class="{
                                    'bg-n-blue-9': child.status === 'open',
                                    'bg-n-green-9': child.status === 'won',
                                    'bg-n-ruby-9': child.status === 'lost',
                                }" />
                                <div class="min-w-0">
                                    <span class="text-xs font-medium text-n-slate-12 truncate block">{{ child.name
                                    }}</span>
                                    <span class="text-[10px] text-n-slate-10">{{ child.pipeline_name }}</span>
                                </div>
                            </div>
                            <span class="text-[10px] font-medium px-1.5 py-0.5 rounded-full flex-shrink-0 ml-2" :class="{
                                'bg-n-blue-3 text-n-blue-11': child.status === 'open',
                                'bg-n-green-3 text-n-green-11': child.status === 'won',
                                'bg-n-ruby-3 text-n-ruby-11': child.status === 'lost',
                            }">{{ child.stage_name }}</span>
                        </div>
                        <template v-if="child.child_deals?.length">
                            <div v-for="grandchild in child.child_deals" :key="'gc-' + grandchild.id"
                                class="flex flex-col" style="padding-left: 14px;">
                                <div
                                    class="flex items-center justify-between py-1.5 px-2 rounded-lg bg-n-alpha-1 hover:bg-n-alpha-2 transition-colors">
                                    <div class="flex items-center gap-1.5 min-w-0">
                                        <span class="i-lucide-corner-down-right size-3 flex-shrink-0 text-n-slate-8" />
                                        <div class="flex-shrink-0 w-1.5 h-1.5 rounded-full" :class="{
                                            'bg-n-blue-9': grandchild.status === 'open',
                                            'bg-n-green-9': grandchild.status === 'won',
                                            'bg-n-ruby-9': grandchild.status === 'lost',
                                        }" />
                                        <div class="min-w-0">
                                            <span class="text-xs font-medium text-n-slate-12 truncate block">{{
                                                grandchild.name }}</span>
                                            <span class="text-[10px] text-n-slate-10">{{ grandchild.pipeline_name
                                            }}</span>
                                        </div>
                                    </div>
                                    <span class="text-[10px] font-medium px-1.5 py-0.5 rounded-full flex-shrink-0 ml-2"
                                        :class="{
                                            'bg-n-blue-3 text-n-blue-11': grandchild.status === 'open',
                                            'bg-n-green-3 text-n-green-11': grandchild.status === 'won',
                                            'bg-n-ruby-3 text-n-ruby-11': grandchild.status === 'lost',
                                        }">{{ grandchild.stage_name }}</span>
                                </div>
                                <template v-if="grandchild.child_deals?.length">
                                    <div v-for="ggchild in grandchild.child_deals" :key="'gg-' + ggchild.id"
                                        class="flex items-center justify-between py-1.5 px-2 rounded-lg bg-n-alpha-1 hover:bg-n-alpha-2 transition-colors mt-1"
                                        style="margin-left: 14px;">
                                        <div class="flex items-center gap-1.5 min-w-0">
                                            <span
                                                class="i-lucide-corner-down-right size-3 flex-shrink-0 text-n-slate-8" />
                                            <div class="flex-shrink-0 w-1.5 h-1.5 rounded-full" :class="{
                                                'bg-n-blue-9': ggchild.status === 'open',
                                                'bg-n-green-9': ggchild.status === 'won',
                                                'bg-n-ruby-9': ggchild.status === 'lost',
                                            }" />
                                            <div class="min-w-0">
                                                <span class="text-xs font-medium text-n-slate-11 truncate block">{{
                                                    ggchild.name }}</span>
                                                <span class="text-[10px] text-n-slate-9">{{ ggchild.pipeline_name
                                                }}</span>
                                            </div>
                                        </div>
                                        <span
                                            class="text-[10px] font-medium px-1.5 py-0.5 rounded-full flex-shrink-0 ml-2"
                                            :class="{
                                                'bg-n-blue-3 text-n-blue-11': ggchild.status === 'open',
                                                'bg-n-green-3 text-n-green-11': ggchild.status === 'won',
                                                'bg-n-ruby-3 text-n-ruby-11': ggchild.status === 'lost',
                                            }">{{ ggchild.stage_name }}</span>
                                    </div>
                                </template>
                            </div>
                        </template>
                    </template>
                </div>
                <div v-else-if="!showChildDealForm" class="flex items-center justify-center py-3">
                    <span class="text-[10px] text-n-slate-9 italic">Nenhuma oportunidade filha</span>
                </div>

                <div v-if="showChildDealForm"
                    class="flex flex-col gap-2.5 mt-2 p-3 rounded-lg border border-n-blue-6 bg-n-blue-2">
                    <div>
                        <label
                            class="text-[10px] font-semibold text-n-slate-10 uppercase tracking-wider block mb-1">Nome</label>
                        <input v-model="childDealForm.name"
                            class="w-full text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-n-blue-7 focus:border-n-blue-8 transition-all placeholder:text-n-slate-8"
                            placeholder="Nome da oportunidade" />
                    </div>
                    <div>
                        <label
                            class="text-[10px] font-semibold text-n-slate-10 uppercase tracking-wider block mb-1">Funil</label>
                        <select :value="childDealForm.pipeline_id"
                            class="w-full text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 py-2 px-3 cursor-pointer focus:outline-none focus:ring-2 focus:ring-n-blue-7 focus:border-n-blue-8 transition-all"
                            style="-webkit-appearance: none; appearance: none; background-image: none;"
                            @change="onChildPipelineChange">
                            <option :value="null">Selecione um funil...</option>
                            <option v-for="p in allPipelines" :key="p.id" :value="p.id">{{ p.name }}</option>
                        </select>
                    </div>
                    <div v-if="childStages.length">
                        <label
                            class="text-[10px] font-semibold text-n-slate-10 uppercase tracking-wider block mb-1">Etapa</label>
                        <select v-model="childDealForm.stage_id"
                            class="w-full text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 py-2 px-3 cursor-pointer focus:outline-none focus:ring-2 focus:ring-n-blue-7 focus:border-n-blue-8 transition-all"
                            style="-webkit-appearance: none; appearance: none; background-image: none;">
                            <option v-for="s in childStages" :key="s.id" :value="s.id">{{ s.name }}</option>
                        </select>
                    </div>
                    <div>
                        <label
                            class="text-[10px] font-semibold text-n-slate-10 uppercase tracking-wider block mb-1">Responsável</label>
                        <select v-model="childDealForm.user_id"
                            class="w-full text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 py-2 px-3 cursor-pointer focus:outline-none focus:ring-2 focus:ring-n-blue-7 focus:border-n-blue-8 transition-all"
                            style="-webkit-appearance: none; appearance: none; background-image: none;">
                            <option :value="null">Sem responsável</option>
                            <option v-for="a in childAgents" :key="a.id" :value="a.id">{{ a.name }}</option>
                        </select>
                    </div>
                    <div class="flex gap-2 pt-1">
                        <button
                            :disabled="!childDealForm.name || !childDealForm.pipeline_id || !childDealForm.stage_id || isCreatingChild"
                            class="flex-1 text-xs font-semibold py-2 px-3 rounded-lg bg-n-blue-9 text-white hover:bg-n-blue-10 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                            @click="createChildDeal">
                            Criar
                        </button>
                        <button
                            class="text-xs font-medium py-2 px-3 rounded-lg bg-n-alpha-3 text-n-slate-11 hover:bg-n-alpha-4 transition-all"
                            @click="cancelChildDealForm">
                            Cancelar
                        </button>
                    </div>
                </div>
            </div>

            <div class="mt-2 rounded-lg border border-n-weak bg-n-solid-2 p-3 shadow-sm">
                <div class="flex items-center justify-between mb-2.5">
                    <div class="flex items-center gap-1.5">
                        <span class="crm-conv-tasks-icon">✓</span>
                        <label class="text-[10px] font-semibold text-n-slate-10 uppercase tracking-wider">
                            Tarefas
                        </label>
                        <span v-if="dealTasks.length" class="text-[9px] font-medium px-1.5 py-0.5 rounded-full"
                            style="background: #f1f5f9; color: #64748b;">
                            {{ completedTasks.length }}/{{ dealTasks.length }}
                        </span>
                        <span v-if="pendingTasks.filter(t => isOverdue(t)).length"
                            class="text-[9px] font-bold px-1.5 py-0.5 rounded-full"
                            style="background: #fef2f2; color: #ef4444;">
                            {{pendingTasks.filter(t => isOverdue(t)).length}} atrasada{{pendingTasks.filter(t =>
                                isOverdue(t)).length > 1 ? 's' : ''}}
                        </span>
                    </div>
                    <button class="crm-conv-tasks-add-btn" @click="showTaskForm = !showTaskForm">
                        <span class="i-lucide-plus size-3" />
                        Nova
                    </button>
                </div>

                <div v-if="dealTasks.length" class="crm-conv-tasks-progress-bar mb-2.5">
                    <div class="crm-conv-tasks-progress-fill"
                        :style="{ width: (completedTasks.length / dealTasks.length * 100) + '%' }" />
                </div>

                <div v-if="showTaskForm"
                    class="flex flex-col gap-2 mb-2.5 p-2.5 rounded-lg border border-n-blue-6 bg-n-blue-2">
                    <input v-model="newTaskForm.title"
                        class="w-full text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-n-blue-7 focus:border-n-blue-8 transition-all placeholder:text-n-slate-8"
                        placeholder="Título da tarefa" @keyup.enter="createTask" />
                    <div class="flex gap-2">
                        <select v-model="newTaskForm.category"
                            class="flex-1 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 py-1.5 px-2 cursor-pointer focus:outline-none focus:ring-2 focus:ring-n-blue-7 transition-all"
                            style="-webkit-appearance: none; appearance: none;">
                            <option v-for="cat in TASK_CATEGORIES" :key="cat.key" :value="cat.key">
                                {{ cat.icon }} {{ cat.label }}
                            </option>
                        </select>
                        <DateTimePicker :value="newTaskForm.due_at" placeholder="Data/hora" confirm-text="Confirmar"
                            @change="val => newTaskForm.due_at = val" />
                    </div>
                    <div class="flex gap-2">
                        <button :disabled="!newTaskForm.title || isCreatingTask"
                            class="flex-1 text-xs font-semibold py-1.5 px-3 rounded-lg bg-n-blue-9 text-white hover:bg-n-blue-10 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                            @click="createTask">
                            Criar
                        </button>
                        <button
                            class="text-xs font-medium py-1.5 px-3 rounded-lg bg-n-alpha-3 text-n-slate-11 hover:bg-n-alpha-4 transition-all"
                            @click="showTaskForm = false">
                            Cancelar
                        </button>
                    </div>
                </div>

                <div v-if="isFetchingTasks" class="flex items-center justify-center py-4">
                    <span class="i-lucide-loader-2 size-4 text-n-slate-10 animate-spin" />
                </div>

                <div v-else-if="pendingTasks.length" class="flex flex-col gap-1 max-h-48 overflow-y-auto"
                    style="scrollbar-width: thin;">
                    <div v-for="task in [...pendingTasks].sort((a, b) => {
                        const aOver = isOverdue(a) ? 0 : 1;
                        const bOver = isOverdue(b) ? 0 : 1;
                        if (aOver !== bOver) return aOver - bOver;
                        if (!a.due_at && !b.due_at) return 0;
                        if (!a.due_at) return 1;
                        if (!b.due_at) return -1;
                        return new Date(a.due_at) - new Date(b.due_at);
                    })" :key="task.id" class="crm-conv-task-row group"
                        :class="isOverdue(task) ? 'crm-conv-task-row--overdue' : ''">
                        <button class="crm-conv-task-toggle"
                            :class="isOverdue(task) ? 'crm-conv-task-toggle--overdue' : ''" @click="toggleTask(task)" />
                        <div class="flex-1 min-w-0">
                            <p class="text-[11px] leading-tight truncate" :title="task.title"
                                :style="{ color: isOverdue(task) ? '#ef4444' : '#1e293b', fontWeight: isOverdue(task) ? 500 : 400 }">
                                {{ task.title }}
                            </p>
                            <div class="flex items-center gap-1.5 mt-1">
                                <span class="crm-conv-task-category-badge">
                                    {{TASK_CATEGORIES.find(c => c.key === task.category)?.icon || '📋'}}
                                    {{TASK_CATEGORIES.find(c => c.key === task.category)?.label || task.category}}
                                </span>
                                <span v-if="task.due_at" class="crm-conv-task-due"
                                    :class="isOverdue(task) ? 'crm-conv-task-due--overdue' : ''">
                                    <span v-if="isOverdue(task)" class="crm-conv-task-due-dot" />
                                    {{ new Date(task.due_at).toLocaleDateString('pt-BR', {
                                        day: '2-digit', month: '2-digit',
                                        hour: '2-digit', minute: '2-digit'
                                    }) }}
                                </span>
                            </div>
                        </div>
                        <button class="crm-conv-task-delete-btn" @click="deleteTask(task)" title="Excluir">
                            <span class="i-lucide-trash-2 size-3" />
                        </button>
                    </div>
                </div>

                <div v-if="!isFetchingTasks && !pendingTasks.length && !showTaskForm"
                    class="flex flex-col items-center justify-center py-4 gap-1.5">
                    <span style="font-size: 20px;">✅</span>
                    <span class="text-[10px] text-n-slate-9">Nenhuma tarefa pendente</span>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
/* Tasks header icon */
.crm-conv-tasks-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 6px;
    background: #dcfce7;
    color: #16a34a;
    font-size: 10px;
    font-weight: 700;
    flex-shrink: 0;
}

/* Add button */
.crm-conv-tasks-add-btn {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-size: 10px;
    font-weight: 600;
    color: #3b82f6;
    padding: 3px 8px;
    border-radius: 6px;
    background: transparent;
    cursor: pointer;
    transition: all 0.15s ease;
}

.crm-conv-tasks-add-btn:hover {
    background: #f1f5f9;
    color: #2563eb;
}

/* Progress bar */
.crm-conv-tasks-progress-bar {
    width: 100%;
    height: 4px;
    border-radius: 9999px;
    background: #f1f5f9;
    overflow: hidden;
}

.crm-conv-tasks-progress-fill {
    height: 100%;
    border-radius: 9999px;
    background: linear-gradient(90deg, #22c55e, #16a34a);
    transition: width 0.4s ease;
    min-width: 0;
}

/* Task row */
.crm-conv-task-row {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 8px 10px;
    border-radius: 8px;
    transition: background 0.15s ease;
}

.crm-conv-task-row:hover {
    background: #f8fafc;
}

.crm-conv-task-row--overdue {
    background: #fef2f2;
}

.crm-conv-task-row--overdue:hover {
    background: #fee2e2;
}

/* Task toggle */
.crm-conv-task-toggle {
    width: 18px;
    height: 18px;
    min-width: 18px;
    min-height: 18px;
    aspect-ratio: 1;
    border-radius: 50%;
    border: 2px solid #cbd5e1;
    background: transparent;
    padding: 0;
    margin: 0;
    box-sizing: border-box;
    flex-shrink: 0;
    cursor: pointer;
    transition: all 0.2s ease;
    -webkit-appearance: none;
    appearance: none;
}

.crm-conv-task-toggle:hover {
    border-color: #22c55e;
    background: #22c55e;
    box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.15);
}

.crm-conv-task-toggle--overdue {
    border-color: #fca5a5;
}

.crm-conv-task-toggle--overdue:hover {
    border-color: #ef4444;
    background: #ef4444;
    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.15);
}

/* Category badge */
.crm-conv-task-category-badge {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    font-size: 9px;
    font-weight: 500;
    padding: 1px 6px;
    border-radius: 9999px;
    background: #f1f5f9;
    color: #64748b;
}

/* Due date */
.crm-conv-task-due {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-size: 9px;
    color: #94a3b8;
}

.crm-conv-task-due--overdue {
    color: #ef4444;
    font-weight: 600;
}

.crm-conv-task-due-dot {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: #ef4444;
    animation: pulse 1.5s infinite;
}

@keyframes pulse {

    0%,
    100% {
        opacity: 1;
    }

    50% {
        opacity: 0.4;
    }
}

/* Delete button */
.crm-conv-task-delete-btn {
    opacity: 0;
    padding: 4px;
    border-radius: 6px;
    color: #ef4444;
    cursor: pointer;
    transition: all 0.15s ease;
    flex-shrink: 0;
    margin-top: 1px;
}

.crm-conv-task-row:hover .crm-conv-task-delete-btn {
    opacity: 1;
}

.crm-conv-task-delete-btn:hover {
    background: #fef2f2;
}

.crm-conv-btn-won {
    background: #dcfce7;
    color: #15803d;
}

.crm-conv-btn-won:hover {
    background: #bbf7d0;
}

.crm-conv-btn-won:active {
    background: #86efac;
}
</style>
